import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getTokenRequestsCount } from '../api/tokens';
import { styled, useTheme } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import PermissionsIcon from '@mui/icons-material/AdminPanelSettings';
import KeyIcon from '@mui/icons-material/Key';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import IconButton from '@mui/material/IconButton';
import { usePermissions } from '../context/PermissionsContext';
import {
  useThemeMode,
  TooltipIconButton,
} from '../components/ui-kit';
import { AvatarWithFallback } from '../components';
import { NotificationsBellWidget, useNotificationsUnreadCount } from './NotificationsWidget';
import { getAvatarUrl } from '../utils/avatarHelpers';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2.5),
  height: 64, // Match standard AppBar height
  color: theme.palette.text.primary,
}));

const openedMixin = (theme, drawerWidth) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: 300,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme, miniDrawerWidth) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: 300,
  }),
  overflowX: 'hidden',
  width: miniDrawerWidth,
});

// Отдельные стили для мобильного и десктопного Drawer
const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) =>
    prop !== 'open' && prop !== 'drawerWidth' && prop !== 'miniDrawerWidth' && prop !== 'isMobile',
})(({ theme, open, drawerWidth, miniDrawerWidth, isMobile }) => ({
  width: isMobile 
    ? (open ? '100%' : miniDrawerWidth) 
    : (open ? drawerWidth : miniDrawerWidth),
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  
  // Мобильные стили
  ...(isMobile && open && {
    width: drawerWidth,
    '& .MuiDrawer-paper': {
      width: drawerWidth,
      maxWidth: '80vw', // Максимальная ширина 80% от ширины экрана
      transition: theme.transitions.create(['width', 'transform'], {
        easing: theme.transitions.easing.easeInOut,
        duration: 300,
      }),
      overflowX: 'hidden',
      position: 'fixed',
      height: '100vh',
      zIndex: 1300, // Высокий z-index для мобильного overlay
      boxSizing: 'border-box',
    },
  }),
  
  ...(isMobile && !open && {
    width: miniDrawerWidth,
    '& .MuiDrawer-paper': {
      ...closedMixin(theme, miniDrawerWidth),
      position: 'fixed',
    },
  }),
  
  // Десктопные стили
  ...(!isMobile && open && {
    ...openedMixin(theme, drawerWidth),
    '& .MuiDrawer-paper': openedMixin(theme, drawerWidth),
  }),
  
  ...(!isMobile && !open && {
    ...closedMixin(theme, miniDrawerWidth),
    '& .MuiDrawer-paper': closedMixin(theme, miniDrawerWidth),
  }),
}));

// Styled component for the Link
const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  display: 'flex',
  width: '100%',
}));

// Updated external toggle button with modern design
const ExternalToggleButton = styled('button')(({ theme, open, drawerWidth, miniDrawerWidth }) => ({
  position: 'fixed',
  top: 20, // Position at level with header title
  left: open ? drawerWidth - 10 : miniDrawerWidth - 10, // Position partially inside sidebar
  right: 'auto', // Reset right positioning
  width: 32,
  height: 32,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 8, // More rounded corners
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  paddingRight: 6,
  zIndex: 25, // Lower z-index to be under sidebar
  boxShadow: theme.shadows[2], // Subtle shadow
  transition: theme.transitions.create(['background-color', 'transform', 'box-shadow', 'border-color', 'left'], {
    duration: 300,
    easing: theme.transitions.easing.easeInOut,
  }),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'scale(1.05)',
    boxShadow: theme.shadows[4],
  },
  '&:active': {
    transform: 'scale(0.95)',
    boxShadow: theme.shadows[1],
  },
  '& svg': {
    fontSize: 18,
    color: theme.palette.text.primary,
    transition: theme.transitions.create(['transform'], {
      duration: 200,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  '&:focus': {
    outline: 'none',
  },
}));


function Sidebar({
  open,
  handleDrawerToggle,
  drawerWidth = 288, // w-72 (288px)
  miniDrawerWidth = 80, // w-20 (80px)
  user,
  logout,
  useExternalToggle = false, // Option to use external toggle button
}) {
  const theme = useTheme();
  const location = useLocation();
  const { hasPermission } = usePermissions();
  const canSeeRequests = hasPermission('auth-service', 'api-token', 'list');

  const { data: requestsCountData } = useQuery({
    queryKey: ['token-requests-count'],
    queryFn: getTokenRequestsCount,
    enabled: canSeeRequests,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const tokenRequestsCount = requestsCountData?.count || 0;
  const isMediaMobile = useMediaQuery(theme.breakpoints.down('lg')); // Changed from 'md' to 'lg' (1024px)
  const [isMobile, setIsMobile] = useState(isMediaMobile);
  const { mode, toggleMode } = useThemeMode();
  const prevPathnameRef = useRef(location.pathname);
  // Bell, page and the unread total come from the notifications widget (notifications-widget/)
  const unreadCount = useNotificationsUnreadCount();

  // Listen for window resize events
  useEffect(() => {
    setIsMobile(isMediaMobile);
  }, [isMediaMobile]);

  // Additional useEffect to handle resize directly
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < theme.breakpoints.values.lg); // Changed to lg (1024px)
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [theme.breakpoints.values.lg]);


  // Auto-close mobile sidebar on route change
  useEffect(() => {
    if (isMobile && open && location.pathname !== prevPathnameRef.current) {
      handleDrawerToggle();
    }
    prevPathnameRef.current = location.pathname;
  }, [location.pathname, isMobile, open, handleDrawerToggle]);

  // Menu items with permission checks
  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/',
      permission: true, // available to all
    },
    {
      text: 'Users',
      icon: <PeopleIcon />,
      path: '/users',
      permission: hasPermission('auth-service', 'user', 'list'),
    },
    {
      text: 'Roles',
      icon: <SecurityIcon />,
      path: '/roles',
      permission: hasPermission('auth-service', 'role', 'list'),
    },
    {
      text: 'Permissions',
      icon: <PermissionsIcon />,
      path: '/permissions',
      permission: hasPermission('auth-service', 'permission', 'list'),
    },
    {
      text: 'API Tokens',
      icon: (
        <Badge badgeContent={tokenRequestsCount || null} color="warning">
          <KeyIcon />
        </Badge>
      ),
      path: '/tokens',
      permission: hasPermission('auth-service', 'api-token', 'list') ||
                 hasPermission('auth-service', 'api-token', 'list-own'),
    },
    {
      text: 'Notifications',
      icon: (
        <Badge badgeContent={unreadCount || null} max={99} color="error">
          <NotificationsIcon />
        </Badge>
      ),
      path: '/notifications',
      permission: true, // available to all
    },
    {
      text: 'Notification Templates',
      icon: <NotificationsActiveIcon />,
      path: '/notification-templates',
      permission: hasPermission('auth-service', 'notification_template', 'list'),
    },
  ];

  // Filter items by permission
  const authorizedMenuItems = menuItems.filter((item) => item.permission);

  const handleLogout = () => {
    logout();
  };

  const renderDrawerContent = () => (
    <>
      <List sx={{ 
        flexGrow: 1, 
        overflowX: 'hidden',
        overflowY: 'auto',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}>
        {authorizedMenuItems.map((item) => (
          <ListItem
            key={item.text}
            disablePadding
            sx={{
              bgcolor:
                location.pathname === item.path
                  ? 'rgba(0, 0, 0, 0.04)'
                  : 'transparent',
              display: 'block',
            }}
          >
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {item.text}
                  </Typography>
                </Box>
              }
               placement="right"
               arrow
               disableHoverListener={!isMobile ? open : true}
             >
               <StyledLink
                 to={item.path}
               >
                <ListItemButton
                  sx={{
                    minHeight: 44, // Minimum 44px touch target
                    justifyContent: open ? 'initial' : 'center',
                    px: 2.5,
                    transition: theme.transitions.create(['background-color', 'transform'], {
                      duration: 300,
                      easing: theme.transitions.easing.easeInOut,
                    }),
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      transform: 'scale(1.01)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: open ? 3 : 'auto',
                      justifyContent: 'center',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      opacity: open ? 1 : 0,
                      display: open ? 'block' : 'none',
                    }}
                  />
                </ListItemButton>
              </StyledLink>
            </Tooltip>
          </ListItem>
        ))}
      </List>

      {/* Bottom actions */}
      <Box sx={{ mt: 'auto' }}>
        <Divider />

        {/* Theme Toggle */}
        <ListItem disablePadding>
          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="body2" fontWeight="bold">
                  {mode === 'dark'
                    ? 'Switch to Light Mode'
                    : 'Switch to Dark Mode'}
                </Typography>
              </Box>
            }
             placement="right"
             arrow
             disableHoverListener={!isMobile ? open : true}
           >
             <ListItemButton
               onClick={toggleMode}
              sx={{
                minHeight: 44,
                justifyContent: open ? 'initial' : 'center',
                px: 2.5,
                transition: theme.transitions.create(['background-color', 'transform'], {
                  duration: 300,
                  easing: theme.transitions.easing.easeInOut,
                }),
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  transform: 'scale(1.01)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
              </ListItemIcon>
              <ListItemText
                primary={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
                sx={{
                  opacity: open ? 1 : 0,
                  display: open ? 'block' : 'none',
                }}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>

        {/* Notification bell — the shared widget; its state and WebSocket live in the widget bundle */}
        {user && (
          <ListItem disablePadding sx={{ display: 'block', px: 1.5 }}>
            <NotificationsBellWidget collapsed={!open} />
          </ListItem>
        )}

        {/* User Profile Section */}
        {user && (
          <ListItem disablePadding>
            {!open ? (
              // Centered avatar for collapsed state
              <Tooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {user.fullName || 'User'}
                    </Typography>
                    <Typography variant="body2">{user.email}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Role:{' '}
                      {user.roles && user.roles.length > 0
                        ? user.roles.map(role => typeof role === 'string' ? role : role.name).join(', ')
                        : 'User'}
                    </Typography>
                  </Box>
                }
                placement="right"
                arrow
                disableHoverListener={false}
              >
                <ListItemButton
                  onClick={() => handleDrawerToggle()}
                  sx={{
                    minHeight: 44,
                    justifyContent: 'center',
                    px: 0,
                    py: 1.5,
                    transition: theme.transitions.create(['background-color', 'transform'], {
                      duration: 300,
                      easing: theme.transitions.easing.easeInOut,
                    }),
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      transform: 'scale(1.01)',
                    },
                  }}
                >
                  <AvatarWithFallback
                    src={getAvatarUrl(user.avatar)}
                    alt={user.fullName || user.email}
                    size={30}
                    sx={{
                      bgcolor: theme.palette.primary.main,
                    }}
                  />
                </ListItemButton>
              </Tooltip>
            ) : (
              // Full profile for expanded state
              <Tooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {user.fullName || 'User'}
                    </Typography>
                    <Typography variant="body2">{user.email}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Role:{' '}
                      {user.roles && user.roles.length > 0
                        ? user.roles.map(role => typeof role === 'string' ? role : role.name).join(', ')
                        : 'User'}
                    </Typography>
                  </Box>
                }
                 placement="right"
                 arrow
                 disableHoverListener={!isMobile ? !open : true}
               >
                 <StyledLink
                   to={`/users/${user?.id}`}
                 >
                  <ListItemButton
                    sx={{
                      minHeight: 44,
                      justifyContent: 'initial',
                      px: 2.5,
                      py: 1.5,
                      transition: theme.transitions.create(['background-color', 'transform'], {
                        duration: 300,
                        easing: theme.transitions.easing.easeInOut,
                      }),
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                        transform: 'scale(1.01)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 3,
                        justifyContent: 'center',
                      }}
                    >
                      <AvatarWithFallback
                        src={getAvatarUrl(user.avatar)}
                        alt={user.fullName || user.email}
                        size={36}
                        sx={{
                          bgcolor: theme.palette.primary.main,
                        }}
                      />
                    </ListItemIcon>
                    <Box
                      sx={{
                        overflow: 'hidden',
                      }}
                    >
                      <Typography
                        variant="body2"
                        noWrap
                        fontWeight="bold"
                        color="text.primary"
                      >
                        {user.fullName || user.email}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        color="text.secondary"
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          display: 'block',
                        }}
                      >
                        {user.email}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </StyledLink>
              </Tooltip>
            )}
          </ListItem>
        )}

        {/* Logout button - always show */}
        {(
          <ListItem disablePadding>
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="body2" fontWeight="bold">
                    Logout
                  </Typography>
                </Box>
              }
               placement="right"
               arrow
               disableHoverListener={!isMobile ? open : true}
             >
               <ListItemButton
                 onClick={handleLogout}
                sx={{
                  minHeight: 44, // Minimum 44px touch target
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  color: theme.palette.error.main,
                  transition: theme.transitions.create(['background-color', 'transform'], {
                    duration: 300,
                    easing: theme.transitions.easing.easeInOut,
                  }),
                  '&:hover': {
                    backgroundColor: theme.palette.error.light + '20', // 20% opacity
                    transform: 'scale(1.02)',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: theme.palette.error.main,
                    transition: theme.transitions.create('transform', {
                      duration: 300,
                      easing: theme.transitions.easing.easeInOut,
                    }),
                  }}
                >
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Logout"
                  sx={{
                    opacity: open ? 1 : 0,
                    color: theme.palette.error.main,
                    transition: theme.transitions.create('opacity', {
                      duration: 300,
                      easing: theme.transitions.easing.easeInOut,
                    }),
                  }}
                />
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
      </Box>
    </>
  );

  // Mobile Layout (below 1024px) - Header bar instead of sidebar
  if (isMobile) {
    return (
      <>
        {/* Mobile Header Bar - hide when drawer is open */}
        {!open && (
          <AppBar
            position="fixed"
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
          >
            <Toolbar>
              <TooltipIconButton
                title="Menu"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
                color="inherit"
              >
                <MenuIcon />
              </TooltipIconButton>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{ flexGrow: 1 }}
              >
                User Management
              </Typography>
              <Box sx={{ width: 44 }}>
                <NotificationsBellWidget collapsed />
              </Box>
            </Toolbar>
          </AppBar>
        )}

        {/* Mobile Drawer - slides in from left */}
        <MuiDrawer
          variant="temporary"
          open={open}
          onClose={handleDrawerToggle}
          sx={{
            '& .MuiDrawer-paper': {
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'hidden', // Предотвращаем горизонтальную прокрутку
              overflowY: 'auto', // Разрешаем только вертикальную прокрутку
            },
          }}
        >
          <DrawerHeader sx={{
            justifyContent: 'space-between',
            px: 2.5,
            overflowX: 'hidden',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}>
            {/* Logo section */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: 2,
                  justifyContent: 'center',
                }}
              >
                <PeopleIcon />
              </ListItemIcon>
              <Typography variant="subtitle1" fontWeight="bold" noWrap>
                User Management
              </Typography>
            </Box>
            
            {/* Close button on the right */}
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                minWidth: 0,
                color: theme.palette.text.primary,
                transition: theme.transitions.create('transform', {
                  duration: 200,
                  easing: theme.transitions.easing.easeInOut,
                }),
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </DrawerHeader>
          <Divider />
          {renderDrawerContent()}
        </MuiDrawer>
      </>
    );
  }

  // Desktop Layout
  return (
    <>
      <Box sx={{ position: 'relative' }}>
        <StyledDrawer
          variant="permanent"
          open={open}
          drawerWidth={drawerWidth}
          miniDrawerWidth={miniDrawerWidth}
          isMobile={isMobile}
          sx={{
            zIndex: 30, // Desktop sidebar: z-30
          }}
        >
          <DrawerHeader>
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
              }}
            >
              <PeopleIcon />
            </ListItemIcon>
            {open && (
              <Typography variant="subtitle1" fontWeight="bold" noWrap>
                User Management
              </Typography>
            )}
          </DrawerHeader>
          <Divider />
          {renderDrawerContent()}
        </StyledDrawer>

        {/* External toggle button - only show on desktop when useExternalToggle is true */}
        {useExternalToggle && (
          <ExternalToggleButton
            open={open}
            drawerWidth={drawerWidth}
            miniDrawerWidth={miniDrawerWidth}
            onClick={handleDrawerToggle}
            aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </ExternalToggleButton>
        )}
      </Box>
    </>
  );
}

export default Sidebar;